import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { 
    Github, 
    Loader2, 
    Check, 
    AlertCircle, 
    Plus, 
    GitBranch,
    Lock,
    Unlock,
    ExternalLink,
    RefreshCw,
    Download,
    Upload
} from 'lucide-react';

const GitHubModal = ({ 
    isOpen, 
    onClose, 
    projectId, 
    projectName,
    onSuccess,
    onImportSuccess
}) => {
    // GitHub connection state
    const [githubStatus, setGithubStatus] = useState({
        connected: false,
        username: null,
        avatar: null
    });
    const [checkingStatus, setCheckingStatus] = useState(true);
    
    // Mode: 'save' or 'import'
    const [mode, setMode] = useState('save');
    
    // Repository state
    const [repos, setRepos] = useState([]);
    const [loadingRepos, setLoadingRepos] = useState(false);
    const [selectedRepo, setSelectedRepo] = useState('');
    const [branch, setBranch] = useState('main');
    const [commitMessage, setCommitMessage] = useState('Update from Mersi.ai');
    
    // Branches state for import
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    
    // Create new repo state
    const [createNewRepo, setCreateNewRepo] = useState(false);
    const [newRepoName, setNewRepoName] = useState('');
    const [newRepoDescription, setNewRepoDescription] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    
    // Operation state
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [importResult, setImportResult] = useState(null);

    // Check GitHub connection status
    useEffect(() => {
        if (isOpen) {
            checkGitHubStatus();
        }
    }, [isOpen]);

    // Load repos when connected
    useEffect(() => {
        if (githubStatus.connected && isOpen) {
            loadRepos();
        }
    }, [githubStatus.connected, isOpen]);

    // Set default new repo name from project name
    useEffect(() => {
        if (projectName) {
            const defaultRepoName = projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            setNewRepoName(prev => prev || defaultRepoName);
        }
    }, [projectName]);

    const checkGitHubStatus = async () => {
        setCheckingStatus(true);
        setError(null);
        try {
            const { data } = await apiService.getGitHubStatus();
            setGithubStatus({
                connected: data.connected,
                username: data.github_username,
                avatar: data.github_avatar
            });
        } catch (err) {
            console.error('Error checking GitHub status:', err);
            setGithubStatus({ connected: false, username: null, avatar: null });
        } finally {
            setCheckingStatus(false);
        }
    };

    const loadRepos = async () => {
        setLoadingRepos(true);
        setError(null);
        try {
            const { data } = await apiService.getGitHubRepos();
            setRepos(data.repos || []);
        } catch (err) {
            console.error('Error loading repos:', err);
            setError('Nu s-au putut încărca repository-urile');
        } finally {
            setLoadingRepos(false);
        }
    };

    // Load branches when repository is selected (for import mode)
    const loadBranches = async (repoName) => {
        if (!repoName || !githubStatus.username) return;
        
        setLoadingBranches(true);
        try {
            const { data } = await apiService.getRepoBranches(githubStatus.username, repoName);
            setBranches(data.branches || []);
            // Set default branch
            if (data.branches && data.branches.length > 0) {
                const defaultBranch = data.branches.includes('main') ? 'main' : 
                                     data.branches.includes('master') ? 'master' : 
                                     data.branches[0];
                setBranch(defaultBranch);
            }
        } catch (err) {
            console.error('Error loading branches:', err);
            setBranches(['main']);
        } finally {
            setLoadingBranches(false);
        }
    };

    // Load branches when repo selection changes in import mode
    useEffect(() => {
        if (mode === 'import' && selectedRepo) {
            loadBranches(selectedRepo);
        }
    }, [selectedRepo, mode]);

    const handleConnectGitHub = async () => {
        try {
            const { data } = await apiService.getGitHubAuthUrl();
            // Store current location to return after auth
            localStorage.setItem('github_auth_return', window.location.href);
            // Redirect to GitHub
            window.location.href = data.authorization_url;
        } catch (err) {
            console.error('Error getting GitHub auth URL:', err);
            setError('Nu s-a putut genera URL-ul de autentificare GitHub');
        }
    };

    const handleDisconnectGitHub = async () => {
        try {
            await apiService.disconnectGitHub();
            setGithubStatus({ connected: false, username: null, avatar: null });
            setRepos([]);
            setSelectedRepo('');
        } catch (err) {
            console.error('Error disconnecting GitHub:', err);
            setError('Nu s-a putut deconecta contul GitHub');
        }
    };

    const handleSaveToGitHub = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const repoName = createNewRepo ? newRepoName : selectedRepo;
            
            if (!repoName) {
                setError('Te rog selectează sau creează un repository');
                setSaving(false);
                return;
            }

            const { data } = await apiService.saveToGitHub({
                project_id: projectId,
                repo_name: repoName,
                branch: branch,
                commit_message: commitMessage,
                create_new_repo: createNewRepo,
                repo_description: newRepoDescription,
                is_private: isPrivate
            });

            setSuccess({
                message: `Proiectul a fost salvat cu succes!`,
                repoUrl: data.repo_url,
                filesPushed: data.files_pushed
            });

            // Reload repos if created new one
            if (createNewRepo) {
                loadRepos();
                setCreateNewRepo(false);
            }

            if (onSuccess) {
                onSuccess(data);
            }
        } catch (err) {
            console.error('Error saving to GitHub:', err);
            setError(err.response?.data?.detail || 'Nu s-a putut salva pe GitHub');
        } finally {
            setSaving(false);
        }
    };

    const handleImportFromGitHub = async () => {
        setImporting(true);
        setError(null);
        setImportResult(null);

        try {
            if (!selectedRepo) {
                setError('Te rog selectează un repository');
                setImporting(false);
                return;
            }

            const { data } = await apiService.importFromGitHub({
                project_id: projectId,
                repo_name: selectedRepo,
                branch: branch
            });

            // Build message including removed files info
            let message = `Import reușit! ${data.files_imported} fișiere importate.`;
            if (data.files_removed && data.files_removed.length > 0) {
                message += ` (${data.files_removed.join(', ')} înlocuit)`;
            }

            setImportResult({
                message: message,
                files: data.file_names,
                filesRemoved: data.files_removed || [],
                repo: data.repo,
                branch: data.branch
            });

            if (onImportSuccess) {
                onImportSuccess(data);
            }
        } catch (err) {
            console.error('Error importing from GitHub:', err);
            setError(err.response?.data?.detail || 'Nu s-a putut importa din GitHub');
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setError(null);
        setSuccess(null);
        setImportResult(null);
        setMode('save');
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Github className="w-5 h-5" />
                        GitHub
                    </DialogTitle>
                    <DialogDescription>
                        Salvează sau importă fișiere din GitHub.
                    </DialogDescription>
                </DialogHeader>

                {checkingStatus ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                ) : !githubStatus.connected ? (
                    // Not connected - show connect button
                    <div className="py-6 text-center space-y-4">
                        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                            <Github className="w-8 h-8 text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Conectează-ți contul GitHub</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Trebuie să te autentifici cu GitHub pentru a continua.
                            </p>
                        </div>
                        <Button onClick={handleConnectGitHub} className="gap-2">
                            <Github className="w-4 h-4" />
                            Conectează GitHub
                        </Button>
                    </div>
                ) : (
                    // Connected - show save/import options
                    <div className="space-y-4">
                        {/* GitHub account info */}
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                {githubStatus.avatar ? (
                                    <img 
                                        src={githubStatus.avatar} 
                                        alt={githubStatus.username}
                                        className="w-8 h-8 rounded-full"
                                    />
                                ) : (
                                    <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                                        <Github className="w-4 h-4 text-green-700" />
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium text-green-800">
                                        Conectat ca @{githubStatus.username}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDisconnectGitHub}
                                className="text-green-700 hover:text-green-800 hover:bg-green-100"
                            >
                                Deconectează
                            </Button>
                        </div>

                        {/* Mode toggle - Save or Import */}
                        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                            <Button
                                variant={mode === 'save' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => { setMode('save'); setError(null); setImportResult(null); }}
                                className="flex-1 gap-2"
                                data-testid="github-save-tab"
                            >
                                <Upload className="w-4 h-4" />
                                Save to GitHub
                            </Button>
                            <Button
                                variant={mode === 'import' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => { setMode('import'); setError(null); setSuccess(null); }}
                                className="flex-1 gap-2"
                                data-testid="github-import-tab"
                            >
                                <Download className="w-4 h-4" />
                                Import from GitHub
                            </Button>
                        </div>

                        {/* Repository selection (common for both modes) */}
                        <div className="space-y-3">
                            {mode === 'save' && (
                                <div className="flex items-center gap-2">
                                    <Label className="text-sm font-medium">Repository</Label>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setCreateNewRepo(!createNewRepo)}
                                        className="h-6 px-2 text-xs"
                                    >
                                        {createNewRepo ? (
                                            <>Selectează existent</>
                                        ) : (
                                            <>
                                                <Plus className="w-3 h-3 mr-1" />
                                                Creează nou
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                            
                            {mode === 'import' && (
                                <Label className="text-sm font-medium">Repository</Label>
                            )}

                            {mode === 'save' && createNewRepo ? (
                                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <Label htmlFor="repoName" className="text-xs text-gray-500">
                                            Nume repository
                                        </Label>
                                        <Input
                                            id="repoName"
                                            value={newRepoName}
                                            onChange={(e) => setNewRepoName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                                            placeholder="my-awesome-project"
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="repoDesc" className="text-xs text-gray-500">
                                            Descriere (opțional)
                                        </Label>
                                        <Input
                                            id="repoDesc"
                                            value={newRepoDescription}
                                            onChange={(e) => setNewRepoDescription(e.target.value)}
                                            placeholder="O scurtă descriere..."
                                            className="mt-1"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="isPrivate"
                                            checked={isPrivate}
                                            onChange={(e) => setIsPrivate(e.target.checked)}
                                            className="rounded"
                                        />
                                        <Label htmlFor="isPrivate" className="text-sm flex items-center gap-1 cursor-pointer">
                                            {isPrivate ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                                            Repository privat
                                        </Label>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative">
                                    <Select value={selectedRepo} onValueChange={setSelectedRepo}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Selectează un repository..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <ScrollArea className="h-[200px]">
                                                {loadingRepos ? (
                                                    <div className="flex items-center justify-center py-4">
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    </div>
                                                ) : repos.length === 0 ? (
                                                    <div className="py-4 text-center text-sm text-gray-500">
                                                        {mode === 'save' ? 'Nu ai repository-uri. Creează unul nou!' : 'Nu ai repository-uri disponibile.'}
                                                    </div>
                                                ) : (
                                                    repos.map((repo) => (
                                                        <SelectItem key={repo.id} value={repo.name}>
                                                            <div className="flex items-center gap-2">
                                                                {repo.private ? (
                                                                    <Lock className="w-3 h-3 text-gray-400" />
                                                                ) : (
                                                                    <Unlock className="w-3 h-3 text-gray-400" />
                                                                )}
                                                                <span>{repo.name}</span>
                                                            </div>
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </ScrollArea>
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={loadRepos}
                                        disabled={loadingRepos}
                                        className="absolute right-10 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${loadingRepos ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Branch selection */}
                        <div>
                            <Label htmlFor="branch" className="text-sm font-medium flex items-center gap-1">
                                <GitBranch className="w-3 h-3" />
                                Branch
                            </Label>
                            {mode === 'import' && branches.length > 0 ? (
                                <Select value={branch} onValueChange={setBranch}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Selectează branch..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {loadingBranches ? (
                                            <div className="flex items-center justify-center py-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            </div>
                                        ) : (
                                            branches.map((b) => (
                                                <SelectItem key={b} value={b}>{b}</SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    id="branch"
                                    value={branch}
                                    onChange={(e) => setBranch(e.target.value)}
                                    placeholder="main"
                                    className="mt-1"
                                />
                            )}
                        </div>

                        {/* Commit message - only for save mode */}
                        {mode === 'save' && (
                            <div>
                                <Label htmlFor="commitMsg" className="text-sm font-medium">
                                    Mesaj commit
                                </Label>
                                <Input
                                    id="commitMsg"
                                    value={commitMessage}
                                    onChange={(e) => setCommitMessage(e.target.value)}
                                    placeholder="Update from Mersi.ai"
                                    className="mt-1"
                                />
                            </div>
                        )}

                        {/* Error message */}
                        {error && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        {/* Success message - for save */}
                        {success && mode === 'save' && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                    <Check className="w-4 h-4" />
                                    {success.message}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs text-green-600">
                                        {success.filesPushed} fișiere salvate
                                    </span>
                                    <a
                                        href={success.repoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-green-700 hover:underline flex items-center gap-1"
                                    >
                                        Vezi pe GitHub
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Import result */}
                        {importResult && mode === 'import' && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                    <Check className="w-4 h-4" />
                                    {importResult.message}
                                </div>
                                <div className="mt-2">
                                    <p className="text-xs text-green-600 mb-1">Fișiere importate:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {importResult.files.slice(0, 10).map((file, idx) => (
                                            <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                {file}
                                            </span>
                                        ))}
                                        {importResult.files.length > 10 && (
                                            <span className="text-xs text-green-600">
                                                +{importResult.files.length - 10} altele
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Închide
                    </Button>
                    {githubStatus.connected && mode === 'save' && (
                        <Button 
                            onClick={handleSaveToGitHub} 
                            disabled={saving || (!createNewRepo && !selectedRepo) || (createNewRepo && !newRepoName)}
                            className="gap-2"
                            data-testid="github-save-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Se salvează...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Salvează pe GitHub
                                </>
                            )}
                        </Button>
                    )}
                    {githubStatus.connected && mode === 'import' && (
                        <Button 
                            onClick={handleImportFromGitHub} 
                            disabled={importing || !selectedRepo}
                            className="gap-2"
                            data-testid="github-import-btn"
                        >
                            {importing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Se importă...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Importă din GitHub
                                </>
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GitHubModal;
